import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SearchHeader from '../../../components/ui/SearchHeader';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';

import vendorDataService from '../../../services/vendorDataService';

const HeroSection = ({ onSearch }) => {
  const navigate = useNavigate();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [userLocation, setUserLocation] = useState(null);
  const [vendorCounts, setVendorCounts] = useState({});

  const heroImages = [
    {
      src: "https://images.unsplash.com/photo-1606800052052-a08af7148866?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80",
      alt: "Beautiful South Asian wedding ceremony with mandap decoration"
    },
    {
      src: "https://images.pexels.com/photos/1024993/pexels-photo-1024993.jpeg?auto=compress&cs=tinysrgb&w=2000&q=80",
      alt: "Elegant mehndi ceremony with traditional decorations"
    },
    {
      src: "https://images.unsplash.com/photo-1519741497674-611481863552?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80",
      alt: "Vibrant sangeet celebration with dancing and music"
    }
  ];

  const quickFilters = [
    { id: 'dj', label: 'DJ & Music', icon: 'Music', category: 'music', count: vendorCounts.music || '0' },
    { id: 'photographer', label: 'Photographers', icon: 'Camera', category: 'photography', count: vendorCounts.photography || '0' },
    { id: 'makeup', label: 'Makeup Artists', icon: 'Palette', category: 'makeup', count: vendorCounts.makeup || '0' },
    { id: 'mehndi', label: 'Mehndi Artists', icon: 'Hand', category: 'mehndi', count: vendorCounts.mehndi || '0' },
    { id: 'venues', label: 'Venues', icon: 'Building', category: 'venue', count: vendorCounts.venue || '0' },
    { id: 'catering', label: 'Catering', icon: 'ChefHat', category: 'catering', count: vendorCounts.catering || '0' }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) =>
        prevIndex === heroImages.length - 1 ? 0 : prevIndex + 1
      );
    }, 5000);

    return () => clearInterval(interval);
  }, [heroImages.length]);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        (error) => {
          console.log('Location access denied');
        }
      );
    }
  }, []);

  // Load vendor counts from database
  useEffect(() => {
    const loadVendorCounts = async () => {
      try {
        const categories = ['music', 'photography', 'makeup', 'mehndi', 'venue', 'catering'];
        const counts = {};

        await Promise.all(categories.map(async (category) => {
          const result = await vendorDataService.getVendors({
            category,
            limit: 1
          });
          if (result.success && result.count) {
            counts[category] = result.count >= 100 ? `${Math.floor(result.count / 10) * 10}+` : result.count.toString();
          }
        }));

        setVendorCounts(counts);
      } catch (error) {
        console.error('Error loading vendor counts:', error);
      }
    };

    loadVendorCounts();
  }, []);

  const handleQuickFilter = (filterId) => {
    onSearch?.(filterId);
  };

  const handleSearch = (query, location) => {
    onSearch?.(query, location);
  };

  const handleLocationChange = (location) => {
    // Handle location change if needed
    console.log('Location changed:', location);
  };

  const handleBrowseAllVendors = () => {
    navigate('/all-vendors');
  };

  return (
    <section className="relative min-h-[600px] lg:min-h-[700px] bg-gradient-to-br from-primary-50 to-secondary-50 overflow-hidden">
      {/* Background Image Carousel */}
      <div className="absolute inset-0">
        {heroImages.map((image, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${index === currentImageIndex ? 'opacity-30' : 'opacity-0'
              }`}
          >
            <Image
              src={image.src}
              alt={image.alt}
              className="w-full h-full object-cover"
            />
          </div>
        ))}
        <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-secondary/20" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 sm:pt-20 pb-16">
        <div className="text-center">
          {/* Main Heading */}
          <div className="mb-8">
            <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-text-primary mb-4">
              Find Your Perfect
              <span className="block text-primary">Wedding Vendors</span>
            </h1>
            <p className="text-lg sm:text-xl text-text-secondary max-w-3xl mx-auto font-body leading-relaxed">
              Connect with verified South Asian wedding professionals across the U.S.
              From mehndi artists to mandap decorators, discover vendors who understand your cultural traditions.
            </p>
          </div>

          {/* Search Section */}
          <div className="mb-12">
            <SearchHeader
              variant="hero"
              onSearch={handleSearch}
              onLocationChange={handleLocationChange}
              placeholder="Search for vendors, services, or locations (e.g. Henna artist, DJ, decorator in NYC)"
            />
          </div>

          {/* Quick Filter Chips */}
          <div className="mb-12">
            <p className="text-sm text-text-secondary font-caption mb-4">
              Popular Categories
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {quickFilters.map((filter) => (
                <button
                  key={filter.id}
                  onClick={() => handleQuickFilter(filter.id)}
                  className="flex items-center space-x-2 px-4 py-2 bg-white/90 backdrop-blur-sm rounded-full border border-border-light hover:bg-white hover:shadow-soft transition-smooth"
                >
                  <Icon name={filter.icon} size={16} color="#667eea" />
                  <span className="text-sm font-medium text-text-primary">{filter.label}</span>
                  <span className="text-xs text-text-muted font-data">({filter.count})</span>
                </button>
              ))}
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              variant="primary"
              size="lg"
              iconName="Search"
              iconSize={20}
              className="px-8"
              onClick={handleBrowseAllVendors}
            >
              Browse All Vendors
            </Button>
            <Button
              variant="outline"
              size="lg"
              iconName="UserPlus"
              iconSize={20}
              className="px-8 bg-white/90 backdrop-blur-sm"
            >
              Join as Vendor
            </Button>
          </div>

          {/* Trust Indicators */}
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-2xl mx-auto">
            <div className="text-center">
              <div className="text-2xl font-bold text-primary font-heading">3,000+</div>
              <div className="text-sm text-text-secondary font-caption">Verified Vendors</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-primary font-heading">50,000+</div>
              <div className="text-sm text-text-secondary font-caption">Happy Couples</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-primary font-heading">25+</div>
              <div className="text-sm text-text-secondary font-caption">Major Cities</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-primary font-heading">4.9★</div>
              <div className="text-sm text-text-secondary font-caption">Average Rating</div>
            </div>
          </div>
        </div>
      </div>

      {/* Image Carousel Indicators */}
      <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex space-x-2 z-20">
        {heroImages.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentImageIndex(index)}
            className={`w-2 h-2 rounded-full transition-smooth ${index === currentImageIndex ? 'bg-white' : 'bg-white/50'
              }`}
          />
        ))}
      </div>

      {/* Location Badge */}
      {userLocation && (
        <div className="absolute top-20 right-4 lg:right-8 bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2 shadow-soft z-20">
          <div className="flex items-center space-x-2">
            <Icon name="MapPin" size={14} color="#667eea" />
            <span className="text-xs text-text-secondary font-caption">Location detected</span>
          </div>
        </div>
      )}
    </section>
  );
};

export default HeroSection;